import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { 
  ArrowLeft, Shield, Ban, Clock, AlertTriangle, UserCheck, Trash2,
  Mail, MapPin, Calendar, MessageCircle, Flag, Eye, Edit, Crown, User, Heart, Ruler
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const USER_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [suspendDays, setSuspendDays] = useState(7);
  const [processing, setProcessing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState({});

  useEffect(() => {
    fetchUserDetail();
    fetchDropdownOptions();
  }, [userId]);

  const fetchDropdownOptions = async () => {
    try {
      const response = await axios.get(`${USER_API}/dropdown-options`);
      setDropdownOptions(response.data);
    } catch (error) {
      console.error('Failed to load dropdown options');
    }
  };

  const fetchUserDetail = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setData(response.data);
      // Initialize edit form with ALL user data
      const user = response.data.user;
      setEditForm({
        email: user.email || '',
        display_name: user.display_name || '',
        bio: user.bio || '',
        age: user.age?.toString() || '',
        height: user.height || '',
        weight: user.weight || '',
        gender: user.gender || '',
        ethnicity: user.ethnicity || '',
        body_type: user.body_type || '',
        position: user.position || '',
        tribe: Array.isArray(user.tribe) ? user.tribe : (user.tribe ? [user.tribe] : []),
        looking_for: Array.isArray(user.looking_for) ? user.looking_for : (user.looking_for ? [user.looking_for] : []),
        // Identity
        pronouns: user.pronouns || '',
        expression: user.expression || '',
        sexuality: user.sexuality || '',
        // Stats - Endowment
        endowment_cut: user.endowment_cut || '',
        endowment_size: user.endowment_size || '',
        // Health
        hiv_status: user.hiv_status || '',
        last_tested: user.last_tested || '',
        prevention_practices: Array.isArray(user.prevention_practices) ? user.prevention_practices : [],
        sexual_practices: Array.isArray(user.sexual_practices) ? user.sexual_practices : [],
        // Lifestyle
        relationship_status: user.relationship_status || '',
        smoking: user.smoking || '',
        drinking: user.drinking || '',
        cannabis: user.cannabis || '',
        meet_at: Array.isArray(user.meet_at) ? user.meet_at : (user.meet_at ? [user.meet_at] : []),
        kinks_freaks: Array.isArray(user.kinks_freaks) ? user.kinks_freaks : [],
        // Membership & status
        membership_tier: user.membership_tier || 'free',
        is_verified: user.is_verified || false,
        is_visible: user.is_visible !== false,
        incognito_mode: user.incognito_mode || false,
        availability_status: user.availability_status || 'just_browsing'
      });
    } catch (error) {
      toast.error('Failed to load user');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSave = async () => {
    setProcessing(true);
    try {
      // Clean up the data before sending - remove empty strings and ensure proper types
      const dataToSend = {};
      
      // String fields - only include if non-empty
      const stringFields = [
        'email', 'display_name', 'bio', 'height', 'weight', 'gender', 'ethnicity',
        'body_type', 'position', 'pronouns', 'expression', 'sexuality',
        'endowment_cut', 'endowment_size', 'hiv_status', 'last_tested',
        'relationship_status', 'smoking', 'drinking', 'cannabis',
        'membership_tier', 'availability_status'
      ];
      
      stringFields.forEach(field => {
        if (editForm[field] && editForm[field] !== '') {
          dataToSend[field] = editForm[field].toString();
        }
      });
      
      // Array fields - include if non-empty array
      const arrayFields = ['tribe', 'looking_for', 'meet_at', 'prevention_practices', 'sexual_practices', 'kinks_freaks'];
      arrayFields.forEach(field => {
        if (Array.isArray(editForm[field]) && editForm[field].length > 0) {
          dataToSend[field] = editForm[field];
        }
      });
      
      // Age needs special handling (empty string becomes null, otherwise string)
      if (editForm.age && editForm.age !== '') {
        dataToSend.age = editForm.age.toString();
      }
      
      // Array fields - only include if non-empty
      if (Array.isArray(editForm.tribe) && editForm.tribe.length > 0) {
        dataToSend.tribe = editForm.tribe;
      }
      if (Array.isArray(editForm.prevention_practices) && editForm.prevention_practices.length > 0) {
        dataToSend.prevention_practices = editForm.prevention_practices;
      }
      if (Array.isArray(editForm.sexual_practices) && editForm.sexual_practices.length > 0) {
        dataToSend.sexual_practices = editForm.sexual_practices;
      }
      
      // Boolean fields - always include
      if (editForm.is_verified !== undefined) {
        dataToSend.is_verified = editForm.is_verified;
      }
      if (editForm.is_visible !== undefined) {
        dataToSend.is_visible = editForm.is_visible;
      }
      if (editForm.incognito_mode !== undefined) {
        dataToSend.incognito_mode = editForm.incognito_mode;
      }
      
      await axios.put(`${API}/users/${userId}`, dataToSend);
      toast.success('User profile updated');
      setShowEditModal(false);
      fetchUserDetail();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' 
        ? errorDetail 
        : Array.isArray(errorDetail) 
          ? errorDetail[0]?.msg || 'Validation error'
          : 'Failed to update user';
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleAction = async () => {
    if (!actionReason.trim() && actionModal !== 'verify' && actionModal !== 'unsuspend' && actionModal !== 'unban') {
      toast.error('Please provide a reason');
      return;
    }

    setProcessing(true);
    try {
      switch (actionModal) {
        case 'warn':
          await axios.post(`${API}/users/${userId}/warn`, {
            user_id: userId,
            reason: actionReason
          });
          toast.success('Warning issued');
          break;
        case 'suspend':
          await axios.post(`${API}/users/${userId}/suspend`, {
            user_id: userId,
            reason: actionReason,
            duration_days: suspendDays
          });
          toast.success(`User suspended for ${suspendDays} days`);
          break;
        case 'unsuspend':
          await axios.post(`${API}/users/${userId}/unsuspend`);
          toast.success('Suspension removed');
          break;
        case 'ban':
          await axios.post(`${API}/users/${userId}/ban`, {
            user_id: userId,
            reason: actionReason
          });
          toast.success('User banned');
          break;
        case 'unban':
          await axios.post(`${API}/users/${userId}/unban`);
          toast.success('Ban removed');
          break;
        case 'verify':
          await axios.post(`${API}/users/${userId}/verify`);
          toast.success('User verified');
          break;
        case 'delete':
          await axios.delete(`${API}/users/${userId}?reason=${encodeURIComponent(actionReason)}`);
          toast.success('User deleted');
          navigate('/admin/users');
          return;
      }
      setActionModal(null);
      setActionReason('');
      fetchUserDetail();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' 
        ? errorDetail 
        : Array.isArray(errorDetail) 
          ? errorDetail[0]?.msg || 'Validation error'
          : 'Action failed';
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('data:') || photo.startsWith('http')) return photo;
    return `data:image/jpeg;base64,${photo}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { user, stats, action_history } = data;
  const status = user.account_status || 'active';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/users')}
            className="text-[#A1A1AA]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Users
          </Button>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowEditModal(true)} className="bg-[#08D9D6] hover:bg-[#08D9D6]/80">
            <Edit className="w-4 h-4 mr-2" />
            Edit Profile
          </Button>
          {!user.is_verified && (
            <Button onClick={() => setActionModal('verify')} variant="outline" className="border-[#08D9D6] text-[#08D9D6]">
              <UserCheck className="w-4 h-4 mr-2" />
              Verify
            </Button>
          )}
          {status === 'active' && (
            <>
              <Button onClick={() => setActionModal('warn')} variant="outline" className="border-yellow-500 text-yellow-500">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Warn
              </Button>
              <Button onClick={() => setActionModal('suspend')} variant="outline" className="border-orange-500 text-orange-500">
                <Clock className="w-4 h-4 mr-2" />
                Suspend
              </Button>
            </>
          )}
          {status === 'suspended' && (
            <Button onClick={() => setActionModal('unsuspend')} className="bg-green-600 hover:bg-green-700">
              Remove Suspension
            </Button>
          )}
          {status !== 'banned' && (
            <Button onClick={() => setActionModal('ban')} variant="destructive">
              <Ban className="w-4 h-4 mr-2" />
              Ban
            </Button>
          )}
          {status === 'banned' && (
            <Button onClick={() => setActionModal('unban')} className="bg-green-600 hover:bg-green-700">
              Remove Ban
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Profile Card */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-[#111] border border-white/10 rounded-xl p-6">
            <div className="text-center">
              <img
                src={getPhotoUrl(user.public_photos?.[0]) || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200'}
                alt=""
                className="w-24 h-24 rounded-full object-cover mx-auto mb-4"
              />
              <div className="flex items-center justify-center gap-2 mb-1">
                <h2 className="text-xl font-bold text-white">{user.display_name || user.username}</h2>
                {user.is_verified && <Shield className="w-5 h-5 text-[#08D9D6]" />}
              </div>
              <p className="text-[#A1A1AA]">@{user.username}</p>
              
              <div className="mt-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  status === 'active' ? 'bg-green-500/10 text-green-500' :
                  status === 'suspended' ? 'bg-yellow-500/10 text-yellow-500' :
                  status === 'banned' ? 'bg-red-500/10 text-red-500' :
                  'bg-gray-500/10 text-gray-500'
                }`}>
                  {status.toUpperCase()}
                </span>
              </div>
            </div>

            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-center gap-3 text-[#A1A1AA]">
                <Mail className="w-4 h-4" />
                <span>{user.email}</span>
              </div>
              {user.age && (
                <div className="flex items-center gap-3 text-[#A1A1AA]">
                  <Calendar className="w-4 h-4" />
                  <span>{user.age} years old</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-[#A1A1AA]">
                <Calendar className="w-4 h-4" />
                <span>Joined {new Date(user.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            {user.bio && (
              <div className="mt-6">
                <p className="text-sm text-[#A1A1AA]">{user.bio}</p>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="bg-[#111] border border-white/10 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Activity Stats</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-[#A1A1AA] flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  Messages Sent
                </span>
                <span className="text-white font-medium">{stats.messages_sent}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#A1A1AA] flex items-center gap-2">
                  <Flag className="w-4 h-4" />
                  Reports Filed
                </span>
                <span className="text-white font-medium">{stats.reports_filed}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#A1A1AA] flex items-center gap-2">
                  <Flag className="w-4 h-4 text-red-500" />
                  Reports Received
                </span>
                <span className="text-white font-medium">{stats.reports_received}</span>
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-red-500 mb-4">Danger Zone</h3>
            <Button 
              onClick={() => setActionModal('delete')}
              variant="destructive" 
              className="w-full"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </Button>
            <p className="text-xs text-[#52525B] mt-2">
              This action cannot be undone. All user data will be permanently deleted.
            </p>
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Photos */}
          {user.public_photos?.length > 0 && (
            <div className="bg-[#111] border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                Photos ({user.public_photos.length} public, {user.private_photos?.length || 0} private)
              </h3>
              <div className="grid grid-cols-4 gap-3">
                {user.public_photos.map((photo, i) => (
                  <img
                    key={i}
                    src={getPhotoUrl(photo)}
                    alt={`Photo ${i + 1}`}
                    className="aspect-square rounded-lg object-cover"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Action History */}
          <div className="bg-[#111] border border-white/10 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Action History</h3>
            {action_history?.length === 0 ? (
              <p className="text-[#A1A1AA] text-center py-8">No actions taken on this user</p>
            ) : (
              <div className="space-y-3">
                {action_history?.map((action) => (
                  <div key={action.id} className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      action.action_type === 'ban' ? 'bg-red-500/10' :
                      action.action_type === 'suspension' ? 'bg-yellow-500/10' :
                      action.action_type === 'warning' ? 'bg-orange-500/10' :
                      'bg-green-500/10'
                    }`}>
                      {action.action_type === 'ban' && <Ban className="w-4 h-4 text-red-500" />}
                      {action.action_type === 'suspension' && <Clock className="w-4 h-4 text-yellow-500" />}
                      {action.action_type === 'warning' && <AlertTriangle className="w-4 h-4 text-orange-500" />}
                      {(action.action_type === 'unsuspension' || action.action_type === 'unban') && (
                        <UserCheck className="w-4 h-4 text-green-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium capitalize">{action.action_type}</p>
                      {action.reason && <p className="text-sm text-[#A1A1AA]">{action.reason}</p>}
                      <p className="text-xs text-[#52525B] mt-1">
                        {new Date(action.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Modal */}
      <Dialog open={!!actionModal} onOpenChange={() => setActionModal(null)}>
        <DialogContent className="bg-[#111] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="capitalize">
              {actionModal === 'verify' ? 'Verify User' :
               actionModal === 'unsuspend' ? 'Remove Suspension' :
               actionModal === 'unban' ? 'Remove Ban' :
               `${actionModal} User`}
            </DialogTitle>
          </DialogHeader>

          {actionModal === 'verify' || actionModal === 'unsuspend' || actionModal === 'unban' ? (
            <p className="text-[#A1A1AA]">
              Are you sure you want to {actionModal === 'verify' ? 'verify' : actionModal === 'unsuspend' ? 'unsuspend' : 'unban'} this user?
            </p>
          ) : (
            <div className="space-y-4">
              {actionModal === 'suspend' && (
                <div className="space-y-2">
                  <Label>Suspension Duration (days)</Label>
                  <Input
                    type="number"
                    value={suspendDays}
                    onChange={(e) => setSuspendDays(parseInt(e.target.value) || 7)}
                    min={1}
                    max={365}
                    className="bg-white/5 border-white/10"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Reason</Label>
                <Textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="Provide a reason for this action..."
                  className="bg-white/5 border-white/10 min-h-[100px]"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionModal(null)}>Cancel</Button>
            <Button 
              onClick={handleAction}
              disabled={processing}
              className={
                actionModal === 'ban' || actionModal === 'delete' ? 'bg-red-600 hover:bg-red-700' :
                actionModal === 'verify' || actionModal === 'unsuspend' || actionModal === 'unban' ? 'bg-green-600 hover:bg-green-700' :
                'bg-[#FF2E63] hover:bg-[#E01E50]'
              }
            >
              {processing ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#08D9D6]" />
              Edit User Profile
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Account Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] border-b border-white/10 pb-2">Account</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#FF2E63]" />
                    Email
                  </Label>
                  <Input
                    value={editForm.email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-white/5 border-white/10"
                    placeholder="Email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Display Name (max 20 chars)</Label>
                  <Input
                    value={editForm.display_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, display_name: e.target.value.slice(0, 20) }))}
                    className="bg-white/5 border-white/10"
                    placeholder="Display name"
                    maxLength={20}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>About Me (max 355 chars)</Label>
                <Textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value.slice(0, 355) }))}
                  className="bg-white/5 border-white/10 min-h-[80px]"
                  placeholder="User bio..."
                  maxLength={355}
                />
                <p className="text-xs text-[#52525B]">{editForm.bio?.length || 0}/355</p>
              </div>
              
              <div className="space-y-2">
                <Label>Looking For</Label>
                <div className="flex flex-wrap gap-2">
                  {dropdownOptions.looking_for?.map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(editForm.looking_for) ? editForm.looking_for : [];
                        if (current.includes(opt)) {
                          setEditForm(prev => ({ ...prev, looking_for: current.filter(i => i !== opt) }));
                        } else {
                          setEditForm(prev => ({ ...prev, looking_for: [...current, opt] }));
                        }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs transition-all ${
                        Array.isArray(editForm.looking_for) && editForm.looking_for.includes(opt)
                          ? 'bg-[#FF2E63]/20 border border-[#FF2E63] text-white'
                          : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Identity */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] border-b border-white/10 pb-2 flex items-center gap-2">
                <User className="w-4 h-4 text-[#08D9D6]" />
                Identity
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <Select
                    value={editForm.gender}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, gender: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.genders?.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Pronouns</Label>
                  <Select
                    value={editForm.pronouns}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, pronouns: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.pronouns?.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Expression</Label>
                  <Select
                    value={editForm.expression}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, expression: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.expression?.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sexuality</Label>
                  <Input
                    value={editForm.sexuality}
                    onChange={(e) => setEditForm(prev => ({ ...prev, sexuality: e.target.value }))}
                    className="bg-white/5 border-white/10"
                    placeholder="How they identify"
                    maxLength={30}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Position</Label>
                  <Select
                    value={editForm.position}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, position: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.positions?.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] border-b border-white/10 pb-2 flex items-center gap-2">
                <Ruler className="w-4 h-4 text-[#FF2E63]" />
                Stats
              </h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Age</Label>
                  <Select
                    value={editForm.age}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, age: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Age" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.ages?.map(a => (
                        <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Height</Label>
                  <Select
                    value={editForm.height}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, height: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Height" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.heights?.map(h => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Weight</Label>
                  <Select
                    value={editForm.weight}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, weight: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Weight" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.weights?.map(w => (
                        <SelectItem key={w} value={w}>{w}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Body Type</Label>
                  <Select
                    value={editForm.body_type}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, body_type: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Body Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.body_types?.map(b => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Ethnicity</Label>
                  <Select
                    value={editForm.ethnicity}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, ethnicity: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Ethnicity" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.ethnicities?.map(e => (
                        <SelectItem key={e} value={e}>{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Relationship Status</Label>
                  <Select
                    value={editForm.relationship_status}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, relationship_status: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.relationship_status?.map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tribe</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {dropdownOptions.tribes?.map(t => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => {
                          const current = Array.isArray(editForm.tribe) ? editForm.tribe : [];
                          if (current.includes(t)) {
                            setEditForm(prev => ({ ...prev, tribe: current.filter(i => i !== t) }));
                          } else {
                            setEditForm(prev => ({ ...prev, tribe: [...current, t] }));
                          }
                        }}
                        className={`px-2 py-1 rounded text-xs capitalize transition-all ${
                          Array.isArray(editForm.tribe) && editForm.tribe.includes(t)
                            ? 'bg-[#FF2E63]/20 border border-[#FF2E63] text-white'
                            : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Endowment</Label>
                <div className="grid grid-cols-2 gap-4">
                  <Select
                    value={editForm.endowment_cut}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, endowment_cut: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Cut/Uncut" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.endowment_cut?.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={editForm.endowment_size}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, endowment_size: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Size" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.endowment_size?.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Connection & Lifestyle */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] border-b border-white/10 pb-2 flex items-center gap-2">
                <Heart className="w-4 h-4 text-[#FF2E63]" />
                Connection & Lifestyle
              </h3>
              
              {/* Meet At - Multi-select */}
              <div className="space-y-2">
                <Label>Meet At</Label>
                <div className="flex flex-wrap gap-2">
                  {dropdownOptions.meet_at?.map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(editForm.meet_at) ? editForm.meet_at : [];
                        if (current.includes(m)) {
                          setEditForm(prev => ({ ...prev, meet_at: current.filter(i => i !== m) }));
                        } else {
                          setEditForm(prev => ({ ...prev, meet_at: [...current, m] }));
                        }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs transition-all ${
                        Array.isArray(editForm.meet_at) && editForm.meet_at.includes(m)
                          ? 'bg-[#FF2E63]/20 border border-[#FF2E63] text-white'
                          : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Substance preferences */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Smoking</Label>
                  <Select
                    value={editForm.smoking}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, smoking: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.smoking?.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Drinking</Label>
                  <Select
                    value={editForm.drinking}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, drinking: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.drinking?.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>420</Label>
                  <Select
                    value={editForm.cannabis}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, cannabis: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.cannabis?.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Sexual Practices - Multi-select */}
              <div className="space-y-2">
                <Label>Sexual Practices</Label>
                <div className="flex flex-wrap gap-2">
                  {dropdownOptions.sexual_practices?.map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(editForm.sexual_practices) ? editForm.sexual_practices : [];
                        if (current.includes(p)) {
                          setEditForm(prev => ({ ...prev, sexual_practices: current.filter(i => i !== p) }));
                        } else {
                          setEditForm(prev => ({ ...prev, sexual_practices: [...current, p] }));
                        }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs transition-all ${
                        Array.isArray(editForm.sexual_practices) && editForm.sexual_practices.includes(p)
                          ? 'bg-[#FF2E63]/20 border border-[#FF2E63] text-white'
                          : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Kinks & Freaks - Multi-select */}
              <div className="space-y-2">
                <Label>Kinks & Freaks</Label>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                  {dropdownOptions.kinks_freaks?.map(k => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(editForm.kinks_freaks) ? editForm.kinks_freaks : [];
                        if (current.includes(k)) {
                          setEditForm(prev => ({ ...prev, kinks_freaks: current.filter(i => i !== k) }));
                        } else {
                          setEditForm(prev => ({ ...prev, kinks_freaks: [...current, k] }));
                        }
                      }}
                      className={`px-2 py-1 rounded text-xs transition-all ${
                        Array.isArray(editForm.kinks_freaks) && editForm.kinks_freaks.includes(k)
                          ? 'bg-[#7000FF]/20 border border-[#7000FF] text-white'
                          : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                      }`}
                    >
                      {k}
                    </button>
                  ))}
                </div>
                {Array.isArray(editForm.kinks_freaks) && editForm.kinks_freaks.length > 0 && (
                  <p className="text-xs text-[#7000FF]">{editForm.kinks_freaks.length} selected</p>
                )}
              </div>
            </div>

            {/* Health & Wellness */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] border-b border-white/10 pb-2 flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#08D9D6]" />
                Health & Wellness
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>HIV Status</Label>
                  <Select
                    value={editForm.hiv_status}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, hiv_status: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.hiv_status?.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Last Tested</Label>
                  <Select
                    value={editForm.last_tested}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, last_tested: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10 max-h-60">
                      {dropdownOptions.last_tested?.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Prevention Practices</Label>
                <div className="flex flex-wrap gap-2">
                  {dropdownOptions.prevention_practices?.map(practice => (
                    <button
                      key={practice}
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(editForm.prevention_practices) ? editForm.prevention_practices : [];
                        if (current.includes(practice)) {
                          setEditForm(prev => ({ ...prev, prevention_practices: current.filter(p => p !== practice) }));
                        } else {
                          setEditForm(prev => ({ ...prev, prevention_practices: [...current, practice] }));
                        }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                        Array.isArray(editForm.prevention_practices) && editForm.prevention_practices.includes(practice)
                          ? 'bg-[#08D9D6]/20 border border-[#08D9D6] text-white'
                          : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                      }`}
                    >
                      {practice}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Membership & Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] border-b border-white/10 pb-2 flex items-center gap-2">
                <Crown className="w-4 h-4 text-[#FFD700]" />
                Membership & Settings
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Membership Tier</Label>
                  <Select
                    value={editForm.membership_tier}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, membership_tier: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10">
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="elite">Elite</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Availability Status</Label>
                  <Select
                    value={editForm.availability_status}
                    onValueChange={(v) => setEditForm(prev => ({ ...prev, availability_status: v }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-white/10">
                      <SelectItem value="just_browsing">Just Browsing</SelectItem>
                      <SelectItem value="ready_to_meet">Ready to Meet</SelectItem>
                      <SelectItem value="available_now">Available Now</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#08D9D6]" />
                    <Label>Verified</Label>
                  </div>
                  <Switch
                    checked={editForm.is_verified}
                    onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, is_verified: checked }))}
                    className="data-[state=checked]:bg-[#08D9D6]"
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-[#A1A1AA]" />
                    <Label>Visible</Label>
                  </div>
                  <Switch
                    checked={editForm.is_visible}
                    onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, is_visible: checked }))}
                    className="data-[state=checked]:bg-[#22c55e]"
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-[#7000FF]" />
                    <Label>Incognito</Label>
                  </div>
                  <Switch
                    checked={editForm.incognito_mode}
                    onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, incognito_mode: checked }))}
                    className="data-[state=checked]:bg-[#7000FF]"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button 
              onClick={handleEditSave}
              disabled={processing}
              className="bg-[#08D9D6] hover:bg-[#08D9D6]/80 text-black"
            >
              {processing ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUserDetail;
